package puzzlekey

import kotlinx.serialization.Serializable

/**
 * The Radar's score for one Scan (ADR-0013 §2), s4: mean p² over the [mask]ed projections over mean p²
 * over all of them. About 1 for anything, about 2.5 for the Puzzle. Public data only: the frame's own
 * projections and the public mask, never the reference.
 */
fun radarScore(projection: DoubleArray, mask: IntArray): Double {
    var masked = 0.0
    for (b in mask) masked += projection[b] * projection[b]
    var all = 0.0
    for (p in projection) all += p * p
    return masked / mask.size / (all / projection.size)
}

/**
 * The Radar's thresholds on the score (ADR-0013 §3): `seal` measures them on the Frame corpus and writes
 * them into `sealed.json`; a redeploy can tune them without a re-seal. Below [cold] the sparkles are a few
 * faint ones; at [hot] they fill the guide. The defaults are ADR-0013's measurements.
 */
@Serializable
class RadarParams(val cold: Double = DEFAULT_RADAR_COLD, val hot: Double = DEFAULT_RADAR_HOT) {
    /** Whether a Scan with this [score] could open, so is worth its KDF candidates (ADR-0013 §4). */
    fun worthTrying(score: Double) = score > cold
}

/** p90 of the other-object frames with the seal mask (ADR-0013's table). */
const val DEFAULT_RADAR_COLD = 1.33

/** p10 of the easy Puzzle frames with sitting-out masks (ADR-0013's table). */
const val DEFAULT_RADAR_HOT = 2.33

/** How much of each new Scan's score the Radar's average takes. */
const val RADAR_SMOOTHING = 0.5

/** The Radar over a run of Scans: their scores, smoothed (an EMA), and how warm that is between cold and hot. */
class Radar(private val params: RadarParams, private val smoothing: Double = RADAR_SMOOTHING) {
    /** The smoothed score; null before the first Scan. */
    var score: Double? = null
        private set

    /** 0 at or below cold, 1 at or above hot, linear between; 0 before the first Scan. */
    val warmth: Double
        get() = score?.let { ((it - params.cold) / (params.hot - params.cold)).coerceIn(0.0, 1.0) } ?: 0.0

    /** Adds one Scan's [score] and returns the smoothed score. */
    fun next(score: Double): Double {
        val smoothed = this.score?.let { it + smoothing * (score - it) } ?: score
        this.score = smoothed
        return smoothed
    }

    fun reset() {
        score = null
    }
}
